import React, { useEffect, useState, useCallback, useRef } from "react";
import { displayName } from "@/lib/names";
import axios from "axios";
import { LoadError } from "@/components/LoadError";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AuthImg } from "@/components/AuthImg";
import { toast } from "sonner";
import { Crown, Plus, Edit3, Trash2, Upload, Loader2, Scroll, Link as LinkIcon } from "lucide-react";
import { EditableTextBlock } from "@/components/EditableTextBlock";

const emptyChief = { name: "", reign_from: "", reign_to: "", biography: "", photo_path: null, person_id: "" };

const ChiefForm = ({ initial, persons, onDone, onCancel }) => {
  const { API } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({ ...emptyChief, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setForm({ ...emptyChief, ...(initial || {}) }); }, [initial]);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_path", data.path);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("chiefName") + " " + t("required")); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        reign_from: form.reign_from === "" ? null : Number(form.reign_from),
        reign_to: form.reign_to === "" ? null : Number(form.reign_to),
        biography: form.biography || "",
        photo_path: form.photo_path || null,
        person_id: form.person_id || null,
      };
      if (initial?.id) await axios.put(`${API}/chiefs/${initial.id}`, payload);
      else await axios.post(`${API}/chiefs`, payload);
      toast.success(t("chiefSaved"));
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="chief-form">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full overflow-hidden bg-[#F0E6D8] border-2 border-[#E3D5CA] flex items-center justify-center">
          {form.photo_path ? (
            <AuthImg path={form.photo_path} className="w-full h-full object-cover" alt="chief" fallback={<Crown className="w-8 h-8 text-[#993D22]" />} />
          ) : (
            <Crown className="w-8 h-8 text-[#993D22]" />
          )}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="chief-photo-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="border-[#993D22] text-[#993D22]" disabled={uploading} data-testid="upload-chief-photo-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {t("uploadPhoto")}
          </Button>
        </div>
      </div>

      <div>
        <Label>{t("chiefName")} *</Label>
        <Input value={form.name} onChange={e => set("name", e.target.value)} required data-testid="chief-name-input" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>{t("reignFrom")}</Label>
          <Input type="number" value={form.reign_from ?? ""} onChange={e => set("reign_from", e.target.value)} placeholder="1920" data-testid="reign-from-input" />
        </div>
        <div>
          <Label>{t("reignTo")}</Label>
          <Input type="number" value={form.reign_to ?? ""} onChange={e => set("reign_to", e.target.value)} placeholder="1955" data-testid="reign-to-input" />
        </div>
      </div>

      <div>
        <Label>{t("linkedPerson")}</Label>
        <Select value={form.person_id || "__none"} onValueChange={v => set("person_id", v === "__none" ? "" : v)}>
          <SelectTrigger data-testid="chief-person-select"><SelectValue placeholder={t("none")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— {t("none")} —</SelectItem>
            {(persons || []).map(p => (
              <SelectItem key={p.id} value={p.id}>{displayName(p)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>{t("chiefBiography")}</Label>
        <Textarea rows={5} value={form.biography || ""} onChange={e => set("biography", e.target.value)} data-testid="chief-biography-input" placeholder={t("oralHistory")} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>{t("cancel")}</Button>
        <Button type="submit" className="btn-terra" disabled={saving} data-testid="save-chief-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {t("save")}
        </Button>
      </div>
    </form>
  );
};

export const ChiefsPage = () => {
  const { API, user } = useAuth();
  const { t } = useLang();
  const [chiefs, setChiefs] = useState([]);
  const [persons, setPersons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const canManage = user && user !== false && (user.role === "elder" || user.role === "admin");

  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const c = await axios.get(`${API}/chiefs`, { timeout: 20000 });
      setChiefs(c.data);
      setLoaded(true);
    } catch { setLoadError(true); return; }
    if (user && (user.validated || user.is_super_admin)) {
      const p = await axios.get(`${API}/persons`, { timeout: 20000 }).catch(() => ({ data: [] }));
      setPersons(p.data);
    }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/chiefs/${id}`);
      toast.success(t("chiefDeleted"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10" data-testid="chiefs-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 fade-in-up">
        <div>
          <div className="label-uppercase text-[#993D22] mb-2 flex items-center gap-2">
            <Crown className="w-3.5 h-3.5" /> {t("chiefs")}
          </div>
          <h1 className="font-serif-heritage text-5xl text-[#261911] leading-tight">{t("chiefsTitle")}</h1>
          <p className="text-sm text-[#6E5B4F] mt-2 max-w-2xl">{t("chiefsSubtitle")}</p>
        </div>
        {canManage ? (
          <Button className="btn-terra" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-chief-btn">
            <Plus className="w-4 h-4 mr-2" /> {t("addChief")}
          </Button>
        ) : (
          <div className="label-uppercase text-[#6E5B4F] italic">{t("reservedToElders")}</div>
        )}
      </div>

      <EditableTextBlock
        settingKey="chieftaincy_tradition"
        title={t("chiefsTradition")}
        description={t("chiefsTraditionDesc")}
        placeholder="Depuis des générations, le chef d'Ekom 1 est désigné parmi les descendants de..."
        canEdit={canManage}
        testIdPrefix="tradition"
      />

      {loadError ? <LoadError onRetry={load} /> : !loaded ? (
        <div className="p-10 text-center text-[#6E5B4F]" data-testid="chiefs-loading">Chargement…</div>
      ) : chiefs.length === 0 ? (
        <div className="heritage-card p-10 text-center text-[#6E5B4F]" data-testid="empty-chiefs">
          <Scroll className="w-10 h-10 mx-auto text-[#D9822B] mb-3" />
          <p className="font-serif-heritage text-xl">{t("noChiefs")}</p>
        </div>
      ) : (
        <ol className="relative border-l-2 border-[#D9822B] pl-6 sm:pl-10 space-y-8 stagger" data-testid="chiefs-timeline">
          {chiefs.map(c => {
            const linked = persons.find(p => p.id === c.person_id);
            return (
              <li key={c.id} className="relative" data-testid={`chief-${c.id}`}>
                <span className="absolute -left-[33px] sm:-left-[49px] top-6 w-6 h-6 rounded-full bg-[#993D22] border-4 border-[#F7F3EE] flex items-center justify-center">
                  <Crown className="w-3 h-3 text-[#FAF7F2]" />
                </span>
                <div className="heritage-card p-6">
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-[#F0E6D8] border-2 border-[#E3D5CA] shrink-0 flex items-center justify-center">
                      {c.photo_path ? (
                        <AuthImg path={c.photo_path} alt={c.name} className="w-full h-full object-cover" fallback={<Crown className="w-10 h-10 text-[#993D22]" />} />
                      ) : (
                        <Crown className="w-10 h-10 text-[#993D22]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                        <div>
                          <h2 className="font-serif-heritage text-3xl text-[#261911]">{c.name}</h2>
                          <div className="label-uppercase text-[#D9822B] mt-1">
                            {c.reign_from || "?"} — {c.reign_to || t("present")}
                          </div>
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-chief-${c.id}`}>
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-red-700 hover:text-red-800" data-testid={`delete-chief-${c.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{t("confirmDelete")}</AlertDialogTitle>
                                  <AlertDialogDescription>{c.name}</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-700 hover:bg-red-800">{t("delete")}</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                      </div>
                      {c.biography && (
                        <p className="text-[#3A2312] whitespace-pre-wrap leading-relaxed mt-3">{c.biography}</p>
                      )}
                      {linked && (
                        <Link to={`/persons/${linked.id}`} className="inline-flex items-center gap-1 text-sm text-[#993D22] font-semibold mt-3" data-testid={`chief-person-link-${c.id}`}>
                          <LinkIcon className="w-3.5 h-3.5" /> {displayName(linked)}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">
              {editing?.id ? t("editChief") : t("addChief")}
            </DialogTitle>
          </DialogHeader>
          <ChiefForm
            initial={editing}
            persons={persons}
            onDone={() => { setOpen(false); load(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChiefsPage;
